import React, { useEffect, useRef, useState } from 'react';
import { Map as MapIcon, Layers, User, MapPin, Loader2, Crosshair, Search, X } from 'lucide-react';

const GKEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
let mapsPromise = null;

function loadGoogleMaps() {
  if (typeof window === 'undefined' || !GKEY) return Promise.resolve(null);
  if (window.google?.maps?.Map) return Promise.resolve(window.google);
  if (mapsPromise) return mapsPromise;
  const existing = document.querySelector('script[data-iris-maps="1"]');
  const waitForReady = () => new Promise((resolve) => {
    // Poll until google.maps.Map (the constructor) is available
    const started = Date.now();
    const tick = () => {
      if (window.google?.maps?.Map && window.google?.maps?.Marker && window.google?.maps?.Geocoder) {
        resolve(window.google);
      } else if (Date.now() - started > 8000) {
        resolve(window.google || null);
      } else {
        setTimeout(tick, 100);
      }
    };
    tick();
  });
  if (existing) {
    mapsPromise = waitForReady();
    return mapsPromise;
  }
  mapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    // Note: no `loading=async` — synchronous classic load ensures Map/Marker/Geocoder are
    // immediately in the window.google.maps namespace when onload fires.
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GKEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-iris-maps', '1');
    script.onload = () => waitForReady().then(resolve);
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return mapsPromise;
}

/**
 * Interactive Google Maps picker.
 * - Click anywhere on the map to drop a pin
 * - Drag the pin to fine-tune
 * - Reverse-geocode → returns { lat, lng, formatted_address, community, area, place_id }
 * - Fallback: iframe embed if no API key OR loading fails
 *
 * Props:
 *   query: string — center hint / fallback iframe query
 *   lat, lng: initial pin position (optional)
 *   onPinChange(details) — called with the geocoded details after pin drop / drag
 *   height: px
 */
export default function MapPicker({
  query, lat, lng, onPinChange, height = 380,
}) {
  const [mode, setMode] = useState('satellite');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [reverseBusy, setReverseBusy] = useState(false);
  const [pinInfo, setPinInfo] = useState(null);
  const [showHelp, setShowHelp] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Convert mode → Google Maps mapTypeId
  const modeMap = { satellite: 'hybrid', roadmap: 'roadmap', terrain: 'terrain' };

  const reverseGeocode = (position) => {
    if (!geocoderRef.current) return;
    setReverseBusy(true);
    geocoderRef.current.geocode({ location: position }, (results, status) => {
      setReverseBusy(false);
      if (status !== 'OK' || !results?.length) return;
      const primary = results[0];
      // Try to pluck a "neighborhood" / "sublocality" for the community
      const neighborhood = primary.address_components?.find(c =>
        c.types.includes('neighborhood') || c.types.includes('sublocality_level_1') || c.types.includes('sublocality')
      )?.long_name;
      const locality = primary.address_components?.find(c => c.types.includes('locality'))?.long_name;
      const details = {
        lat: position.lat(),
        lng: position.lng(),
        formatted_address: primary.formatted_address,
        community: neighborhood || locality,
        area: neighborhood,
        emirate: locality,
        place_id: primary.place_id,
      };
      setPinInfo(details);
      onPinChange?.(details);
    });
  };

  useEffect(() => {
    if (!GKEY || !containerRef.current) return;
    let cancelled = false;
    loadGoogleMaps().then((g) => {
      if (cancelled || !g?.maps?.Map || !containerRef.current) return;
      const center = { lat: lat || 25.115, lng: lng || 55.155 };
      const map = new g.maps.Map(containerRef.current, {
        center,
        zoom: (lat && lng) ? 17 : 12,
        mapTypeId: modeMap[mode],
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
        zoomControl: true,
      });
      mapRef.current = map;
      geocoderRef.current = new g.maps.Geocoder();

      const marker = new g.maps.Marker({
        map,
        position: center,
        draggable: true,
        visible: !!(lat && lng),
        animation: g.maps.Animation.DROP,
      });
      markerRef.current = marker;

      map.addListener('click', (e) => {
        marker.setPosition(e.latLng);
        marker.setVisible(true);
        setShowHelp(false);
        reverseGeocode(e.latLng);
      });
      marker.addListener('dragend', () => {
        setShowHelp(false);
        reverseGeocode(marker.getPosition());
      });

      if (lat && lng) {
        setShowHelp(false);
        setTimeout(() => reverseGeocode(marker.getPosition()), 300);
      }

      // Attach Places Autocomplete to the search input
      if (searchInputRef.current && g.maps.places) {
        const ac = new g.maps.places.Autocomplete(searchInputRef.current, {
          fields: ['geometry', 'name', 'formatted_address', 'place_id', 'address_components'],
          componentRestrictions: { country: ['ae', 'gb'] }, // UAE + UK
        });
        autocompleteRef.current = ac;
        ac.addListener('place_changed', () => {
          const place = ac.getPlace();
          if (!place?.geometry?.location) return;
          const loc = place.geometry.location;
          map.panTo(loc);
          map.setZoom(17);
          marker.setPosition(loc);
          marker.setVisible(true);
          setShowHelp(false);
          setSearchTerm(place.formatted_address || place.name || '');
          reverseGeocode(loc);
        });
      }

      setLoading(false);
    }).catch((e) => {
      console.warn('Maps failed to load:', e);
      setLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to external lat/lng change (e.g. Places Autocomplete picked something)
  useEffect(() => {
    if (!mapRef.current || !markerRef.current || !lat || !lng) return;
    const pos = { lat, lng };
    mapRef.current.setCenter(pos);
    mapRef.current.setZoom(17);
    markerRef.current.setPosition(pos);
    markerRef.current.setVisible(true);
    setShowHelp(false);
  }, [lat, lng]);

  // Change map type when mode toggle clicked
  useEffect(() => {
    if (mapRef.current) mapRef.current.setMapTypeId(modeMap[mode]);
    // eslint-disable-next-line
  }, [mode]);

  // If no key → fallback iframe
  if (!GKEY) {
    const q = encodeURIComponent((query || 'Dubai') + ' Dubai');
    const src = `https://maps.google.com/maps?q=${q}&t=k&z=15&output=embed`;
    return (
      <div className="glass overflow-hidden">
        <div className="p-3 flex items-center justify-between border-b border-white/60">
          <div className="overline flex items-center gap-2"><MapIcon size={12}/> Location</div>
          <div className="text-[10px] text-slate-500">Set REACT_APP_GOOGLE_MAPS_KEY for interactive picker</div>
        </div>
        <iframe title="map-fallback" src={src} width="100%" height={height} style={{ border: 0 }} loading="lazy"/>
      </div>
    );
  }

  return (
    <div className="glass overflow-hidden" data-testid="map-picker">
      <div className="p-3 flex items-center justify-between gap-3 border-b border-white/60 flex-wrap">
        <div className="overline flex items-center gap-2 shrink-0">
          <MapIcon size={12}/> Location · search or click to pin
          {reverseBusy && <Loader2 size={11} className="animate-spin text-slate-400"/>}
        </div>
        <div className="flex gap-1 bg-white/60 rounded-full p-0.5 shrink-0">
          {[
            { key: 'satellite', label: 'Satellite', icon: Layers },
            { key: 'roadmap',   label: 'Map',       icon: MapIcon },
            { key: 'terrain',   label: 'Terrain',   icon: User },
          ].map(({ key, label, icon: Ic }) => (
            <button key={key}
              onClick={() => setMode(key)}
              data-testid={`map-mode-${key}`}
              className={`px-3 py-1 rounded-full text-xs flex items-center gap-1 transition ${mode === key ? 'bg-[#1A2530] text-white' : 'text-slate-600 hover:bg-white/80'}`}>
              <Ic size={11}/> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="relative">
        <div ref={containerRef} style={{ width: '100%', height }} data-testid="map-picker-canvas"/>

        {/* Search bar overlay */}
        <div className="absolute top-3 left-3 right-3 pointer-events-none z-10 flex justify-center">
          <div className="pointer-events-auto w-full max-w-md relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
            <input
              ref={searchInputRef}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              placeholder="Search address, building or community…"
              data-testid="map-search-input"
              className="w-full pl-10 pr-9 py-2.5 rounded-full bg-white/95 border border-white shadow-lg text-sm outline-none focus:ring-2 focus:ring-[#679286]/40"
            />
            {searchTerm && (
              <button
                onClick={() => { setSearchTerm(''); searchInputRef.current?.focus(); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-200 grid place-items-center hover:bg-slate-300"
                aria-label="Clear"
              ><X size={12}/></button>
            )}
          </div>
        </div>

        {loading && (
          <div className="absolute inset-0 grid place-items-center bg-slate-100/70 text-sm text-slate-500">
            <Loader2 size={16} className="animate-spin"/>
          </div>
        )}
        {showHelp && !loading && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-slate-900/85 text-white text-xs shadow-lg flex items-center gap-2 pointer-events-none">
            <Crosshair size={12}/> Search above, or click anywhere on the map to drop a pin
          </div>
        )}
      </div>

      {pinInfo && (
        <div className="p-3 bg-white/60 border-t border-white/60" data-testid="map-picker-info">
          <div className="flex items-start gap-2">
            <MapPin size={14} className="text-[#679286] mt-0.5 shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-800 line-clamp-2">{pinInfo.formatted_address}</div>
              <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                {pinInfo.lat.toFixed(5)}, {pinInfo.lng.toFixed(5)}
                {pinInfo.community && <> · {pinInfo.community}</>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
